import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KeyRound, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminChangePasswordModal = ({ open, onOpenChange }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("New passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success("Password updated.");
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="admin-change-password-modal" className="max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-rr-navy flex items-center gap-2">
            <KeyRound size={18} className="text-rr-orange" /> Change Password
          </DialogTitle>
          <DialogDescription>Update your admin password for this website.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Current password"
            data-testid="admin-change-password-current-input"
            className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
          />
          <input
            type="password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password (min. 8 characters)"
            data-testid="admin-change-password-new-input"
            className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
          />
          <input
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            data-testid="admin-change-password-confirm-input"
            className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
          />
          {error && <p data-testid="admin-change-password-error" className="text-xs text-rr-red font-bold">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-change-password-submit-button"
            className="w-full bg-rr-navy text-white font-bold py-3 rounded-full hover:bg-rr-orange transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : "Update Password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
